import UserAvatar from 'components/Avatar';
import {
  SubmitterName,
  StatusBox,
  TemplateCnt,
  TaskNameLink,
  Dot,
  LastModifiedText,
  AnonymousAvatar,
} from './style';
import { useDispatch } from 'react-redux';
import { Box, Stack, Typography } from '@mui/material';
import { iconToComponentMapping } from 'pages/checklistV2/mappings';
import { setDialog } from 'store/actions/dialogActions';
import moment from 'moment-timezone';
import CustomButton from 'components/Button/CustomButton';
import { useWorkspaceHook } from 'utils/CustomHooks/useWorkspaceHook';
import { getTimeZone } from 'utils/globalFunction';
import GlobeIcon from 'components/Icons/globeIcon';

const MuiIcon = ({ name, ...rest }) => {
  const IconComponent = iconToComponentMapping[name];
  return IconComponent ? <IconComponent {...rest} /> : null;
};

export const Template = ({ template }) => {
  return (
    <TemplateCnt direction="row" alignItems="center" gap={1}>
      <Box
        className="checklistIcon"
        style={{ background: template?.icon?.color }}
      >
        <MuiIcon name={template?.icon?.icon} sx={{ fontSize: '28px' }} />
      </Box>
      <Typography
        fontWeight="700"
        fontSize="14px"
        lineHeight="19.12px"
        color="#000000"
      >
        {template?.name}
      </Typography>
    </TemplateCnt>
  );
};

export const TaskName = ({ task }) => {
  const dispatch = useDispatch();

  const redirectToTaskDetails = (task) => {
    if (task?.id) {
      dispatch(
        setDialog({
          open: true,
          dialogId: 'taskDetail',
          data: {
            taskId: task?.id,
          },
        }),
      );
    }
  };

  return (
    <TaskNameLink onClick={() => redirectToTaskDetails(task)}>
      {task?.name || '--'}
    </TaskNameLink>
  );
};

export const Submitter = ({ submitter, anonymousName }) => {
  return (
    <Stack direction="row" alignItems="center">
      {submitter?.id ? (
        <UserAvatar width={24} height={24} userId={submitter?.id} />
      ) : (
        <AnonymousAvatar>
          <GlobeIcon style={{ color: '#fff', fontSize: '18px' }} />
        </AnonymousAvatar>
      )}

      <SubmitterName>
        {submitter?.fullName ?? anonymousName ?? '-'}
      </SubmitterName>
    </Stack>
  );
};

export const LastModified = ({ lastModified }) => {
  const tz: string = getTimeZone();
  return (
    <LastModifiedText>
      {moment.tz(lastModified, tz).format('MMM DD, YYYY • hh:mm A')}
    </LastModifiedText>
  );
};

export const Actions = ({ data }) => {
  const { navigateWithWorkspaceUrl } = useWorkspaceHook();
  const { canReview, SubmissionId, CurrentStepId } = data;

  const goToReviewMode = () => {
    navigateWithWorkspaceUrl(
      `/submission/${SubmissionId}/approval/${CurrentStepId}?canReview=${canReview}`,
    );
  };

  return canReview ? (
    <CustomButton
      sx={{ width: 70, height: 34, borderRadius: '6px' }}
      variant="contained"
      onClick={goToReviewMode}
    >
      Review
    </CustomButton>
  ) : (
    <CustomButton
      sx={{ width: 70, height: 34, borderRadius: '6px' }}
      variant="outlined"
      onClick={goToReviewMode}
    >
      View
    </CustomButton>
  );
};

export const Status = ({ status, steps = '' }) => {
  return (
    <>
      {status === 'Pending Approval' && (
        <StatusBox
          sx={{
            backgroundColor: 'rgba(220, 229, 234, 1)',
            border: '1px solid rgba(207, 216, 220, 1)',
          }}
        >
          <Dot sx={{ backgroundColor: '#90A4AE' }} />
          <Typography className="label">Pending Approval {steps}</Typography>
        </StatusBox>
      )}

      {status === 'Changes Requested' && (
        <StatusBox
          sx={{
            backgroundColor: 'rgba(255, 243, 224, 1)',
            border: '1px solid rgba(255, 224, 130, 1)',
          }}
        >
          <Dot sx={{ backgroundColor: '#FF9800' }} />
          <Typography className="label">Changes Requested {steps}</Typography>
        </StatusBox>
      )}

      {status === 'Rejected' && (
        <StatusBox
          sx={{
            backgroundColor: 'rgba(255, 235, 238, 1)',
            border: '1px solid rgba(255, 205, 210, 1)',
          }}
        >
          <Dot sx={{ backgroundColor: '#EF5350' }} />
          <Typography className="label">Rejected</Typography>
        </StatusBox>
      )}

      {status === 'Approved' && (
        <StatusBox
          sx={{
            backgroundColor: 'rgba(232, 245, 233, 1)',
            border: '1px solid rgba(200, 230, 201, 1)',
          }}
        >
          <Dot sx={{ backgroundColor: 'rgba(76, 175, 80, 1)' }} />
          <Typography className="label">Approved</Typography>
        </StatusBox>
      )}
    </>
  );
};
